import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { extractErrorMessage } from "../api/client";
import { getSchema, RecordUnit, SchemaField, SchemaResponse } from "../api/schema";

// Schema reference. Renders the canonical field list paim exports +
// search environments expose. The operator's
// src/paim/operator/dataset_schema.py module is the single
// source-of-truth — the handoff doc, this page, and any future docs
// all read from the same data via /api/v1/schema. When the schema
// changes, you change the module and both surfaces follow.

type Tab = "all" | "program" | "page";

const TAB_LABEL: Record<Tab, string> = {
  all: "All fields",
  program: "Combined NDJSON (per-program)",
  page: "NDJSON (per-page)",
};

const TAB_BLURB: Record<Tab, string> = {
  all:
    "Every field that may appear in a paim search-environment document. " +
    "Each row notes which record unit(s) it belongs to.",
  program:
    "Fields present on combined-NDJSON records — one per program (pages " +
    "joined). Loaded into a search environment with record_unit=program.",
  page:
    "Fields present on per-page NDJSON records — one per source XML page. " +
    "Loaded into a search environment with record_unit=page.",
};

export function SchemaReference() {
  const [data, setData] = useState<SchemaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    getSchema()
      .then(setData)
      .catch((e) => setError(extractErrorMessage(e)));
  }, []);

  if (error)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  if (!data)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Container>
    );

  const fields =
    tab === "program" ? data.program : tab === "page" ? data.page : data.fields;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5">Schema reference</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Field-level schema of every document indexed into a paim search
        environment. The handoff doc that ships with a provisioned env
        renders from the same source.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as Tab)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        <Tab value="all" label={TAB_LABEL.all} />
        <Tab value="program" label={TAB_LABEL.program} />
        <Tab value="page" label={TAB_LABEL.page} />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {TAB_BLURB[tab]}
      </Typography>

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>Field</TableCell>
              <TableCell sx={{ width: "15%" }}>Type</TableCell>
              <TableCell>Description</TableCell>
              {tab === "all" && <TableCell sx={{ width: "15%" }}>Record unit</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((f) => (
              <FieldRow key={f.name} field={f} showUnits={tab === "all"} />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Container>
  );
}

function FieldRow({ field, showUnits }: { field: SchemaField; showUnits: boolean }) {
  return (
    <TableRow>
      <TableCell>
        <Typography
          variant="body2"
          component="code"
          sx={{
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          }}
        >
          {field.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {field.type}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{field.description}</Typography>
      </TableCell>
      {showUnits && (
        <TableCell>
          <Stack direction="row" spacing={0.5}>
            {field.units.map((u: RecordUnit) => (
              <Chip
                key={u}
                size="small"
                label={u}
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            ))}
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
}
